import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal, Card } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAllFacilities } from "../../redux/facilitySlice";
import { FaTrash, FaCalendarAlt, FaMapMarkerAlt, FaPhone, FaStar, FaEye, FaFutbol } from "react-icons/fa";
import DefaultImage from "../../assets/svgs/file.svg";
import "../facilities/facilities.css";
import { toast } from 'react-toastify';
import { deleteFacility, getFacilityReviews } from "../../services/api";
import LazyImage from "../LazyImage";
import "../LazyImage.css";
import ReviewsModal from "./ReviewsModal";
import sportIcons from "./addfacility/sportsicon";
import SportFallbackIcon from "../../assets/svgs/sport-icon.svg";

// Add SkeletonFacilityCard component
const SkeletonFacilityCard = () => {
  return (
    <Col lg={4} md={6} sm={12} className="mb-4">
      <div className="facility-card">
        <div className="skeleton-image"></div>
        <div className="facility-content">
          <div className="skeleton-header">
            <div className="skeleton-title"></div>
            <div className="skeleton-rating"></div>
          </div>

          {[1, 2, 3].map((index) => (
            <div key={index} className="skeleton-info-section">
              <div className="skeleton-circle"></div>
              <div className="skeleton-text-content">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            </div>
          ))}

          <div className="skeleton-button"></div>
        </div>
      </div>
    </Col>
  );
};

const FacilityCard = ({ facility, onDelete }) => {
  const [imgError, setImgError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [cardRating, setCardRating] = useState(null);
  const navigate = useNavigate();

  const formatAddress = (location) => {
    if (!location?.name) return "No location specified";
    return location.name.split(',').slice(0, 1).join(',').trim();
  };

  const handleCardClick = () => {
    navigate("/edit-facility", { state: { facility } });
  };

  useEffect(() => {
    let mounted = true;

    const setFromFacilityIfAvailable = () => {
      const r = facility?.rating;
      if (r === 0) {
        setCardRating(0);
        return true;
      }
      if (typeof r === 'number' && !isNaN(r)) {
        setCardRating(r);
        return true;
      }
      return false;
    };

    const fetchAndComputeRating = async () => {
      try {
        const res = await getFacilityReviews(facility._id, 1, 1000);
        if (!mounted) return;
        const reviews = Array.isArray(res?.data?.reviews) ? res.data.reviews : [];
        if (reviews.length === 0) {
          setCardRating(0);
          return;
        }
        const sum = reviews.reduce((acc, rv) => acc + (Number(rv.rating) || 0), 0);
        const avg = sum / reviews.length;
        setCardRating(Number.isFinite(avg) ? parseFloat(avg.toFixed(1)) : 0);
      } catch (e) {
        if (mounted) setCardRating(0);
      }
    };

    if (!setFromFacilityIfAvailable()) {
      fetchAndComputeRating();
    }

    return () => { mounted = false; };
  }, [facility?._id, facility?.rating]);

  const handleDelete = async () => {
    try {
      await deleteFacility(facility._id);
      toast.success("Facility deleted successfully");
      onDelete(facility._id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting facility:", error);
      toast.error(error.response?.data?.message || "Failed to delete facility");
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return DefaultImage;
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('/facility')) {
      return `https://goplayupdated.s3.us-east-1.amazonaws.com${imageUrl}`;
    }
    return imageUrl;
  };

  return (
    <Col lg={4} md={6} sm={12} className="mb-4">
      <div className="facility-card">
        <div className="facility-image">
          <LazyImage
            src={imgError ? DefaultImage : getImageUrl(facility?.coverPhoto)}
            alt={facility?.name || "Facility"}
            onError={() => setImgError(true)}
          />
          <div style={{ color: facility.isActive ? '#38b000' : 'red' }} className="available-tag">
            <span style={{ backgroundColor: facility?.isActive ? '#38b000' : 'red' }} className="dot"></span>
            {facility?.isActive ? 'Active' : 'InActive'}
          </div>
          <button
            className="delete-btn facili"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(true);
            }}
          >
            <FaTrash color="white" size={16} />
          </button>
        </div>

        <div className="facility-content">
          <div className="facility-header">
            <h3 className="facility-title">{facility.name}</h3>
            <div className="facility-rating-section">
              <div className="facility-rating">
                <FaStar className="rating-star" />
                <span className="rating-value">{cardRating && cardRating > 0 ? cardRating.toFixed(1) : 'N/A'}</span>
              </div>
              <button
                className="view-reviews-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReviewsModal(true);
                }}
              >
                <FaEye size={12} />
                Reviews
              </button>
            </div>
          </div>

          <div className="info-section">
            <div className="icon-circle">
              <FaCalendarAlt size={14} />
            </div>
            <div className="info-content">
              <div className="info-label">Date</div>
              <div className="info-value">
                {facility.contractTenure?.from && facility.contractTenure?.to
                  ? `${new Date(facility.contractTenure.from).toLocaleDateString('en-GB')} to ${new Date(facility.contractTenure.to).toLocaleDateString('en-GB')}`
                  : "No dates specified"}
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="icon-circle">
              <FaMapMarkerAlt size={14} />
            </div>
            <div className="info-content">
              <div className="info-label">Location</div>
              <div className="info-value" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {formatAddress(facility.location)}
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="icon-circle">
              <FaPhone size={14} />
            </div>
            <div className="info-content">
              <div className="info-label">Contact</div>
              <div className="info-value">
                {facility.contact || facility.phone || "No contact info"}
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="icon-circle">
              <FaFutbol size={14} />
            </div>
            <div className="info-content">
              <div className="info-label">Sports</div>
              <div className="sports-pitches icons-row">
                {Array.isArray(facility.availableSport) && facility.availableSport.length > 0 ? (
                  <>
                    {facility.availableSport.slice(0, 3).map((sport, index) => {
                      const name = typeof sport === 'string' ? sport : sport?.name;
                      const remoteIcon = typeof sport === 'object' ? (sport?.icon || sport?.iconUrl || sport?.image || sport?.icon_url) : null;
                      const iconSrc = remoteIcon || sportIcons[name] || SportFallbackIcon;
                      return (
                        <img key={index} src={iconSrc} alt={name || 'Sport'} className="sport-mini-icon" />
                      );
                    })}
                    {facility.availableSport.length > 3 && (
                      <span className="more-sports" style={{ marginTop: '-2%' }}>+{facility.availableSport.length - 3}</span>
                    )}
                  </>
                ) : (
                  <span style={{ marginTop: '-1%', fontSize: '12px', color: '#666' }}>No sports available</span>
                )}
              </div>
            </div>
          </div>

          <button className="view-details-btn" onClick={handleCardClick}>
            Edit Facility
          </button>
          <button
            className="view-details-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/ViewFacility", { state: { facility } });
            }}
            style={{ marginTop: '10px', backgroundColor: '#6c757d' }}
          >
            View Facility
          </button>
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Facility</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "16px" }}>Are you sure you want to delete {facility.name} Facility?</p>
          <p className="text-danger" style={{ fontSize: "14px", color: "black" }}>This action is irreversible and will delete all related data including bookings, slots, and pitches.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="btn-modal-delete-slot w-100" style={{ display: "flex", justifyContent: "center", alignItems: "center" }} onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <ReviewsModal
        show={showReviewsModal}
        onHide={() => setShowReviewsModal(false)}
        facility={facility}
      />
    </Col>
  );
};

// Format contract tenure dates
const formatContractTenure = (tenure) => {
  if (!tenure) return "No contract dates";

  try {
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";

      // Handle different date formats
      let date;
      if (dateString.includes('-')) {
        const parts = dateString.split('-').map(num => parseInt(num, 10));
        if (parts.length === 3) {
          // Check if it's YYYY-MM-DD format (ISO format)
          if (parts[0] > 12) {
            // First part is year (YYYY-MM-DD)
            const [year, month, day] = parts;
            date = new Date(year, month - 1, day);
          } else {
            // First part is month (MM-DD-YYYY)
            const [month, day, year] = parts;
            date = new Date(year, month - 1, day);
          }
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }

      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    };

    const fromDate = formatDate(tenure.from || tenure.start);
    const toDate = formatDate(tenure.to || tenure.end);
    return `${fromDate} - ${toDate}`;
  } catch (error) {
    console.error("Error formatting contract tenure:", error);
    return "Invalid date format";
  }
};

const Facilities = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const { facilities: reduxFacilities, loading, error, initialized } = useSelector((state) => {
    return state.facilities;
  });
  const facilities = Array.isArray(reduxFacilities) ? reduxFacilities : [];
  const showError = error && initialized;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dispatch(fetchAllFacilities()).unwrap();
      } catch (error) {
        console.error("Error fetching facilities:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  const handleDeleteFacility = (facilityId) => {
    dispatch(fetchAllFacilities());
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFacilities = facilities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(facilities.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render skeleton cards
  const renderSkeletons = () => {
    return (
      <Row>
        {[1, 2, 3, 4, 5, 6].map((index) => (
          <SkeletonFacilityCard key={`skeleton-${index}`} />
        ))}
      </Row>
    );
  };

  // Render empty state
  const renderEmpty = () => {
    return (
      <Col xs={12}>
        <Card className="empty-card">
          <Card.Body className="text-center">
            <LazyImage
              src={DefaultImage}
              alt="No Facilities"
              className="empty-image"
            />
            <Card.Title>No Facilities Available</Card.Title>
            <Card.Text className="text-muted">
              You haven't added any facilities yet. Click the button below to add your first facility.
            </Card.Text>
            <Button
              variant="success"
              className="mt-3"
              onClick={() => navigate("/addfacility")}
            >
              Add Facility
            </Button>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <Container className="facilities-container">
      <Row>
        <h1 className="container-title">My Facilities</h1>
        <Col className="text-end">
          <button className="btn-view-slots" onClick={() => navigate("/slots")}>
            View Slots &#8594;
          </button>
        </Col>
      </Row>

      <Row>
        {loading ? (
          renderSkeletons()
        ) : showError ? (
          <Col xs={12}>
            <Card className="error-card">
              <Card.Body className="text-center">
                <LazyImage
                  src={DefaultImage}
                  alt="Error"
                  className="error-image"
                />
                <Card.Title>Unable to Load Facilities</Card.Title>
                <Card.Text className="text-muted">
                  There was a problem loading your facilities. Please try again later.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ) : facilities.length === 0 ? (
          renderEmpty()
        ) : (
          <>
            {currentFacilities.map((facility) => (
              <FacilityCard
                key={facility._id}
                facility={facility}
                onDelete={handleDeleteFacility}
              />
            ))}
          </>
        )}
      </Row>

      {!loading && !showError && facilities.length > 0 && totalPages > 1 && (
        <Row className="mt-4">
          <Col xs={12}>
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index + 1}
                    className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Facilities;
